import React from 'react';
import { useTranslation } from 'react-i18next';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faRepeat } from '@fortawesome/pro-solid-svg-icons/faRepeat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import '../i18n/config';
import { PublicSitterItem, Sitter, SitterExperience, UserStats } from 'us-web-services';
import { StarRating } from '../Rating/StarRating';
import { getRate } from '../utils/rate';

type Props = {
  sitter: Sitter | PublicSitterItem;
  reviewSize?: string;
  numberOfChildren?: number;
  hideExperience?: boolean;
};

const DAY_MILLISECONDS = 1000 * 60 * 60 * 24;
const NO_EXPERIENCE = { id: 0, shortLabel: '0' };
const NOT_WILLING_ID = 8;

export const SitterCardDetails = ({ numberOfChildren, reviewSize = 'base', sitter, hideExperience }: Props): JSX.Element => {
  const { t } = useTranslation();

  const isFullSitter = (s: Sitter | PublicSitterItem): s is Sitter => 'userStats' in s;

  // Properties specific to Sitter or PublicSitterItem types
  let experiences: SitterExperience | undefined;
  let userStats: UserStats | undefined;
  let yearExperience: string | undefined;

  if (isFullSitter(sitter)) {
    experiences = sitter.experiences;
    userStats = sitter.userStats;
  } else {
    yearExperience = sitter.yearExperience;
  }

  // Common properties on both sitter types
  const { rates, rating, repeatFamilies, reviewCount } = sitter;

  const getSitterExperience = (): string => {
    if (experiences) {
      const infant = experiences.infant?.id && experiences.infant.id < NOT_WILLING_ID ? experiences.infant : NO_EXPERIENCE;
      const school = experiences.school?.id && experiences.school.id < NOT_WILLING_ID ? experiences.school : NO_EXPERIENCE;
      const toddler = experiences.toddler?.id && experiences.toddler.id < NOT_WILLING_ID ? experiences.toddler : NO_EXPERIENCE;
      const infantOrSchool = infant.id > school.id ? infant : school;

      return infantOrSchool.id > toddler.id ? (infantOrSchool?.shortLabel || '') : (toddler?.shortLabel || '');
    }
      return yearExperience || '';

  }

  const rate = getRate(rates, numberOfChildren);
  const sitterExperience = getSitterExperience();
  const hideSitterExperience = (sitterExperience === '0' || !sitterExperience) ? true : hideExperience;
  const showLastAccess = userStats?.lastAccessDate && userStats?.lastAccessLabel
    && new Date().getTime() - new Date(userStats.lastAccessDate).getTime() < DAY_MILLISECONDS * 30;
  return (
      <div className='flex flex-col px-2 pb-2'>
        <StarRating rating={rating ?? 0} reviewCount={reviewCount ?? 0} size={reviewSize} className='mb-1' />
        <div className='flex flex-row mb-2'>
          <FontAwesomeIcon icon={faRepeat} className='mr-2 text-sm self-center' />
          <span className='font-semibold text-xs'>{t('components.sitterCard.repeatFamilies', { repeatFamilies })}</span>
        </div>
        <div className='flex flex-row mb-2'>
          {!hideSitterExperience && <span className='font-semibold text-xs mr-5'>{t('components.sitterCard.yearsExp', { yearsExp: sitterExperience })}</span>}
          { rate > 0 && <span className='font-bold text-xs text-emerald-700 justify-self-start'>{t('components.sitterCard.rate', { rate })}</span> }
        </div>
        {showLastAccess &&
          <div className='flex flex-row'>
            <FontAwesomeIcon icon={faBolt} className='mr-2 text-xs self-center' />
            <span className='font-light text-center text-xs'>
              {t('components.sitterCard.activeAgo', { activeAgo: userStats?.lastAccessLabel })}
            </span>
          </div>
        }
      </div>
  );
};
